import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSiteConfig } from "../../context/SiteConfigContext";
import { LANDING_CONTAINER_PADDED } from "../../constants/landingPageLayout";
import { PLATFORM_NAME } from "../../constants/brand";
import BrandWordmark from "../common/BrandWordmark";

const NAV_LINK_BASE =
  "text-[16px] font-medium leading-none transition-colors duration-200";

const NAV_LINK_INACTIVE = "text-[#64748B] hover:text-[#0F172A]";
const NAV_LINK_ACTIVE = "font-semibold text-[#4F46E5]";

const navLinkStyle = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 500,
};

const Header = ({ overlay = false, isStatic = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { signupAllowed } = useSiteConfig();
  const { pathname } = useLocation();

  const isActivePath = (path) =>
    pathname === path || pathname.startsWith(`${path}/`);

  const getNavLinkClass = (path, extra = "") =>
    `${NAV_LINK_BASE} ${
      isActivePath(path) ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE
    }${extra ? ` ${extra}` : ""}`;

  const ctaLabel = signupAllowed ? "Get Started" : "Contact us";
  const ctaTo = signupAllowed ? "/signup" : "/contact";

  const positionClass = isStatic
    ? "relative"
    : overlay
    ? "fixed top-0 left-0 right-0"
    : "sticky top-0";

  return (
    <header
      className={`no-scrollbar z-50 w-full transition-all duration-300 ${positionClass} border-b border-white/20 bg-gradient-to-b from-white/80 to-white/[0.08] shadow-[0px_1px_4px_0px_#00000005] backdrop-blur-[8px]`}
    >
      <div className={`relative ${LANDING_CONTAINER_PADDED} py-4 lg:py-[25px]`}>
        <div className="relative flex h-[38px] w-full items-center justify-between gap-4">
          <Link
            to="/"
            aria-label={`${PLATFORM_NAME} home`}
            className="group flex h-[34.44px] cursor-pointer items-center gap-[9.39px] lg:w-[173px]"
          >
            <img
              src="/favicon.svg"
              alt=""
              width={34.44}
              height={34.44}
              className="h-[34.44px] w-[34.44px] shrink-0 transition-transform duration-300 group-hover:scale-105"
              decoding="async"
            />
            <BrandWordmark
              accent="solid"
              className="select-none text-[22px] font-bold leading-none tracking-[-0.02em] text-[#0F172A]"
            />
          </Link>

          <nav
            className="absolute left-1/2 hidden h-[19px] w-[270px] -translate-x-1/2 items-center justify-center gap-[24px] lg:flex"
            aria-label="Main navigation"
          >
            <Link
              to="/features"
              className={getNavLinkClass("/features")}
              style={navLinkStyle}
              aria-current={isActivePath("/features") ? "page" : undefined}
            >
              Features
            </Link>
            <Link
              to="/how-it-works"
              className={getNavLinkClass("/how-it-works", "whitespace-nowrap")}
              style={navLinkStyle}
              aria-current={isActivePath("/how-it-works") ? "page" : undefined}
            >
              How It Works
            </Link>
            <Link
              to="/pricing"
              className={getNavLinkClass("/pricing")}
              style={navLinkStyle}
              aria-current={isActivePath("/pricing") ? "page" : undefined}
            >
              Pricing
            </Link>
          </nav>

          <div className="hidden h-[38px] w-[187px] shrink-0 items-center justify-end gap-[14px] lg:flex">
            <Link
              to="/login"
              className={getNavLinkClass(
                "/login",
                "inline-flex h-[19px] w-[43px] items-center justify-center"
              )}
              style={navLinkStyle}
              aria-current={isActivePath("/login") ? "page" : undefined}
            >
              Login
            </Link>
            <Link
              to={ctaTo}
              className={`inline-flex h-[38px] items-center justify-center rounded-[14px] bg-[#0F172A] p-[14px] text-center text-[16px] font-medium leading-none text-white transition-colors duration-200 hover:bg-[#1E293B] ${
                signupAllowed ? "w-[130px]" : "min-w-[130px] whitespace-nowrap"
              }`}
              style={navLinkStyle}
            >
              {ctaLabel}
            </Link>
          </div>

          <button
            type="button"
            className="rounded-xl p-2 text-slate-700 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 lg:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              {isMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {isMenuOpen ? (
          <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 flex animate-fadeIn flex-col gap-4 rounded-2xl border border-slate-100 bg-white/95 p-5 shadow-xl backdrop-blur-md lg:hidden">
            <Link
              to="/features"
              className={getNavLinkClass("/features", "py-2")}
              style={navLinkStyle}
              aria-current={isActivePath("/features") ? "page" : undefined}
              onClick={() => setIsMenuOpen(false)}
            >
              Features
            </Link>
            <Link
              to="/how-it-works"
              className={getNavLinkClass("/how-it-works", "py-2")}
              style={navLinkStyle}
              aria-current={isActivePath("/how-it-works") ? "page" : undefined}
              onClick={() => setIsMenuOpen(false)}
            >
              How It Works
            </Link>
            <Link
              to="/pricing"
              className={getNavLinkClass("/pricing", "py-2")}
              style={navLinkStyle}
              aria-current={isActivePath("/pricing") ? "page" : undefined}
              onClick={() => setIsMenuOpen(false)}
            >
              Pricing
            </Link>
            <div className="my-1 h-px w-full bg-slate-100" />
            <div className="flex items-center justify-between gap-4">
              <Link
                to="/login"
                className={getNavLinkClass("/login")}
                style={navLinkStyle}
                aria-current={isActivePath("/login") ? "page" : undefined}
                onClick={() => setIsMenuOpen(false)}
              >
                Login
              </Link>
              <Link
                to={ctaTo}
                className="inline-flex h-[38px] min-w-[130px] items-center justify-center rounded-[14px] bg-[#0F172A] px-[14px] text-[16px] font-medium leading-none text-white transition-colors hover:bg-[#1E293B]"
                style={navLinkStyle}
                onClick={() => setIsMenuOpen(false)}
              >
                {ctaLabel}
              </Link>
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
};

export default Header;
